import { FC, MouseEvent, RefCallback, useCallback } from "react";
import { Link } from "react-router";
import { LinkItemProps as ExternalLinkItemProps } from "../LinkItem";
import { getHtmlPropsFromBaseProps } from "../../../utilities/getHtmlPropsFromBaseProps";
import { Tooltip } from "../../Tooltip";
import { MenuCloseEventHandler } from "../MenuCloseEventHandler";

import styles from "../styles.module.css";

/**
 * The `LinkItem` component properties.
 */
export interface LinkItemProps extends ExternalLinkItemProps {
  /**
   * Called when the item has been clicked and is closing the menu.
   */
  readonly onClose?: MenuCloseEventHandler;

  /**
   * The aria-label for the menu link item element.
   */
  readonly "aria-label"?: string;
}

/**
 * The renderable menu item.
 */
export const LinkItem: FC<LinkItemProps> = (props) => {
  const { to, target, disabled = false, type = "normal", onClose, tooltip, children, "aria-label": ariaLabel } = props;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Item, "item", type, { disabled }, "option", "content"],
    testIdPrefix: "menu-item",
  });

  const clickHandler = useCallback(
    (ev: MouseEvent) => {
      if (disabled) {
        ev.preventDefault();
        return;
      }

      onClose?.();
    },
    [disabled],
  );

  const content = (ref?: RefCallback<HTMLAnchorElement>) => {
    if (typeof to === "string" && (to.startsWith("http://") || to.startsWith("https://"))) {
      return (
        <a
          {...baseHtmlProps}
          href={to}
          target={target}
          ref={ref}
          tabIndex={disabled ? -1 : 0}
          onClick={clickHandler}
          role="menuitem"
          aria-disabled={disabled || undefined}
          aria-label={ariaLabel}
        >
          {children}
        </a>
      );
    } else {
      return (
        <Link
          {...baseHtmlProps}
          to={to}
          target={target}
          ref={ref}
          tabIndex={disabled ? -1 : 0}
          onClick={clickHandler}
          role="menuitem"
          aria-disabled={disabled || undefined}
          aria-label={ariaLabel}
        >
          {children}
        </Link>
      );
    }
  };

  return (
    <>
      {tooltip && <Tooltip content={tooltip}>{content}</Tooltip>}
      {!tooltip && content()}
    </>
  );
};
